import * as React from 'react';
import Square from '../AnnotationShapes/Square';
import SquareGlyph from '../../assets/SquareGlyph.svg';
import FreehandGlyph from '../../assets/FreehandGlyph.svg';
import FreeHand from '../AnnotationShapes/Freehand';
import ArrowGlyph from '../../assets/ArrowGlyph.svg';
import Arrow from '../AnnotationShapes/Arrow';
import ColorPickerContainer from '../ColorPicker/ColorPickerContainer';
import Strings from '../../globals/Strings';
import {ControlButton, TscButtonType, Glyphs} from '@techsmith/tsc-cloud-style-guide';
import {AnnotationModes} from '../../globals/Constants';
import Themeable from '../../types/Themeable';
import {AlertModal} from '@techsmith/tsc-cloud-style-guide';

export default ({annotationColor, canvas, annotationMode, setAnnotationMode, pausePlayer, themeName, reviewFeedbackFocused, annotationExists}: AnnotationToolbarProps) => {
   const [isDrawing, setIsDrawing] = React.useState(false);
   const [isSquareSelected, setIsSquareSelected] = React.useState(false);
   const [isArrowSelected, setIsArrowSelected] = React.useState(false);
   const [showAlert, setShowAlert] = React.useState(false);

   React.useEffect(() => {
      if (annotationMode !== AnnotationModes.edit && (isDrawing || isArrowSelected || isSquareSelected)) {
         resetState();
      }
   }, [annotationMode]);

   const resetState = (): void => {
      canvas.defaultCursor = 'default';
      setIsDrawing(false);
      setIsArrowSelected(false);
      setIsSquareSelected(false);
      Square.turnOffMouseEventListeners(canvas);
      Arrow.turnOffMouseEventListeners(canvas);
      FreeHand.endDrawing(canvas);
   };

   const startAnnotating = (): void => {
      resetState();

      if (annotationMode !== AnnotationModes.edit) {
         setAnnotationMode(AnnotationModes.edit);
         reviewFeedbackFocused();
         pausePlayer();
      }
   };

   const onClickSquare = (): void => {
      if (isSquareSelected) {
         resetState();
         return;
      }

      startAnnotating();
      setIsSquareSelected(true);
      Square.turnOnMouseEventListeners(canvas, annotationColor);
   };

   const onClickArrow = (): void => {
      if (isArrowSelected) {
         resetState();
         return;
      }

      startAnnotating();
      setIsArrowSelected(true);
      Arrow.turnOnMouseEventListeners(canvas, annotationColor);
   };

   const onClickFreehand = (): void => {
      if (isDrawing) {
         resetState();
         return;
      }

      startAnnotating();
      setIsDrawing(true);
      FreeHand.startDrawing(canvas, annotationColor);
   };

   const onColorChange = (color: string): void => {
      canvas.trigger('colorchange', color);

      if (isArrowSelected) {
         Arrow.turnOnMouseEventListeners(canvas, color);
      } else if (isSquareSelected) {
         Square.turnOnMouseEventListeners(canvas, color);
      }
   };

   const clearCanvas = (): void => {
      setAnnotationMode(AnnotationModes.none);
      resetState();
      setShowAlert(false);
   };

   const onCancelDeletion = (): void => {
      setShowAlert(false);
   };

   const deleteAnnotations = (): void => {
      setShowAlert(true);
   };

   return (
      <div className="edit-annotation">
         {annotationMode === AnnotationModes.edit && <ControlButton themeName={themeName}
                                                                    buttonType={TscButtonType.tertiary}
                                                                    isFullWidth={false}
                                                                    onClick={annotationExists ? deleteAnnotations : clearCanvas}
                                                                    label={Strings.labels.annotations.clear}
                                                                    testId="annotation-clear-button"
                                                                    glyph={<Glyphs.Close16x16/>}/>
         }
         <ControlButton themeName={themeName}
                        buttonType={TscButtonType.secondary}
                        isFullWidth={false}
                        onClick={onClickSquare}
                        title={Strings.labels.annotations.square}
                        testId="annotation-square-button"
                        glyph={<SquareGlyph/>}
                        active={isSquareSelected && annotationMode === AnnotationModes.edit}/>
         <ControlButton themeName={themeName}
                        buttonType={TscButtonType.secondary}
                        isFullWidth={false}
                        onClick={onClickArrow}
                        title={Strings.labels.annotations.arrow}
                        testId="annotation-arrow-button"
                        glyph={<ArrowGlyph/>}
                        active={isArrowSelected && annotationMode === AnnotationModes.edit}/>
         <ControlButton themeName={themeName}
                        buttonType={TscButtonType.secondary}
                        isFullWidth={false}
                        onClick={onClickFreehand}
                        title={Strings.labels.annotations.freehand}
                        testId="annotation-freehand-button"
                        glyph={<FreehandGlyph/>}
                        active={isDrawing && annotationMode === AnnotationModes.edit}/>
         <ColorPickerContainer onColorChange={onColorChange}/>
         {showAlert && <AlertModal title={Strings.labels.clearAnnotation}
                           onClose={onCancelDeletion}
                           closeContent={Strings.labels.cancel}
                           onConfirm={clearCanvas}
                           confirmContent={Strings.labels.annotations.clear}
                           confirmButtonType={TscButtonType.destructive}
                           onDismiss={onCancelDeletion}
                           dismissContent={Strings.labels.cancel}
                           testId="clear-canvas-alert">
            <div className="alert">{Strings.labels.confirmCanvasDeletionText}</div>
         </AlertModal>}
      </div>
   );
};

export interface AnnotationToolbarProps extends Themeable {
   canvas: any;
   annotationColor: string;
   annotationMode: AnnotationModes;
   annotationExists: boolean;
   reviewFeedbackFocused(): void;
   setAnnotationMode(annotationMode: AnnotationModes): void;
   pausePlayer(): void;
}

export interface AnnotationToolbarState {
   isDrawing: boolean;
   isSquareSelected: boolean;
   isArrowSelected: boolean;
   showAlert: boolean;
}
